import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import TabellaLista from '../../../components/Tabella/TabellaLista';
import { ChipFilter, filtroMultiplo } from '../../../components/Tabella/ChipFilter';
import MyApiClient from '../../../../api-client/MyApiClient';
import { sports } from '../../../components/Util/enum';

/*
 * Corsi — elenco corsi: la stessa pagina dell'Elenco corsi (colonne, ricerca, filtri a chip,
 * freccia di apertura) e gli stessi dati veri del gestionale. Cambia solo dove porta la riga:
 * alla scheda corso della nuova gestione.
 */

const STATO_OPTIONS = ['Bozza', 'Attivo', 'Non attivo'];

export const CorsiListPage = () => {
    const navigate = useNavigate();
    const [data_json, setDataJson] = useState(null);
    const [loadingShowed, setLoadingShowed] = useState(false);
    const [sportFilter, setSportFilter] = useState('');
    const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS);

    const sportOptions = useMemo(
        () => [...new Set((data_json || []).map((row) => row.sportType).filter(Boolean))].sort(),
        [data_json],
    );

    const filteredData = useMemo(
        () =>
            data_json &&
            data_json.filter(
                (row) =>
                    (!sportFilter || row.sportType === sportFilter) &&
                    filtroMultiplo(statoFilter, STATO_OPTIONS, row.status_label?.label),
            ),
        [data_json, sportFilter, statoFilter],
    );

    const apriScheda = (row) => navigate(`/struttura/corsi/${row.id}`);

    const data_column = [
        {
            Header: 'Nome',
            Footer: 'Nome',
            // La ricerca «nome o maestro» trova anche il maestro: la colonna contiene entrambi, mostra il nome.
            id: 'name',
            accessor: (row) => [row.name, row.coachName].filter(Boolean).join(' '),
            Cell: ({ row }) => row.original.name,
            // Il nome del corso è la colonna che si legge: ha più spazio delle altre
            larghezza: '38%',
        },
        {
            Header: 'N. Squadre',
            Footer: 'N. Squadre',
            accessor: 'squadre',
            align: 'center',
            disableGlobalFilter: true,
            Cell: ({ value }) => value || 0,
            disableFilters: true,
        },
        {
            Header: 'N. Vendite',
            Footer: 'N. Vendite',
            accessor: 'purchasesCount',
            align: 'center',
            disableGlobalFilter: true,
            Cell: ({ value }) => value || 0,
            disableFilters: true,
        },
        {
            Header: 'Sport',
            Footer: 'Sport',
            accessor: 'sportType',
            disableGlobalFilter: true,
        },
        {
            Header: 'Stato',
            Footer: 'Stato',
            accessor: 'status_label',
            disableGlobalFilter: true,
            Cell: ({ value }) => (
                <div className="d-flex align-items-center">
                    <i className={`fa fa-circle me-1 ${value ? value.class : ''}`}></i>
                    {value ? value.label : ''}
                </div>
            ),
            disableFilters: true,
        },
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (!tokenDetailsString) return;
                const token = JSON.parse(tokenDetailsString).idToken;
                const apiClient = new MyApiClient();

                setLoadingShowed(true);
                // Le squadre si chiedono tutte in una volta e si contano per corso: una
                // chiamata per riga sarebbe stata un'attesa per ogni corso in elenco.
                const [bookingCourses, squadre] = await Promise.all([
                    apiClient.getBookingCourses(token),
                    apiClient.getBookingCourseTeams(token),
                ]);
                setLoadingShowed(false);

                if (!bookingCourses) return;
                const squadrePerCorso = {};
                (Array.isArray(squadre) ? squadre : []).forEach((t) => {
                    const corso = t.bookingCourseId || t.courseId;
                    if (corso) squadrePerCorso[corso] = (squadrePerCorso[corso] || 0) + 1;
                });
                setDataJson(bookingCourses.map((element) => {
                    const status = element.status === 'DRAFT' ? { label: 'Bozza', class: 'text-warning' }
                        : element.status === 'PURCHASABLE' ? { label: 'Attivo', class: 'text-success' }
                        : element.status === 'CANCELLED' ? { label: 'Non attivo', class: 'text-primary' }
                        : null;
                    return {
                        id: element.id,
                        name: element.name,
                        sportType: sports.find((s) => s.value === element.sportType)?.label || '',
                        type: element.type,
                        status: element.status,
                        status_label: status,
                        squadre: squadrePerCorso[element.id] || 0,
                        purchasesCount: element.purchasesCount,
                        coachName: element.coachName,
                    };
                }));
            } catch (error) {
                setLoadingShowed(false);
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    return (
        <>
            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            <TabellaLista
                pageList={true}
                hasAddMore={true}
                linkAddMore={'/struttura/corsi/nuovo'}
                labelAddMore={'Nuovo corso'}
                hasSearch={true}
                searchPlaceholder={'Cerca per nome o maestro'}
                hasFilter={false}
                filterControl={
                    <div className="sid-chip-filters">
                        {sportOptions.length > 1 && <ChipFilter label="Sport" options={sportOptions} value={sportFilter} onChange={setSportFilter} asDropdown />}
                        <ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
                    </div>
                }
                data_column={data_column}
                data_json={filteredData}
                openColumn={'name'}
                openRow={(row) => apriScheda(row)}
                sort_by={'name'}
                sort_type={'asc'}
            />
        </>
    );
};

export default CorsiListPage;
